import useAuth from '@app/auth/use-auth';
import { ccfRoutes } from '@app/ccf/ccf-routes';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { scroller } from 'react-scroll';
import { ReportType } from './types';

export function scrollTo(name: string, options?: object) {
  scroller.scrollTo(name, {
    duration: 500,
    delay: 0,
    smooth: 'true',
    offset: -72,
    ...options,
  });
}

export function getReportsPathMap(): Record<
  ReportType,
  {
    list: string;
    report: string;
    imports?: string;
    create: string;
    setup: string;
    metric: string;
    edit?: string;
    editMetric?: string;
    foodData?: string;
    packagingData?: string;
    publish?: string;
    foodItem?: string;
    foodImport?: string;
    foodImportUpload?: string;
    foodReview?: string;
    foodImporting?: string;
    packagingImport?: string;
    packagingImportUpload?: string;
    packagingImportReview?: string;
    packagingImporting?: string;
    importList?: string;
    review?: string;
  }
> {
  return {
    ccf: {
      list: ccfRoutes.COMPANY_REPORTS,
      report: ccfRoutes.COMPANY_REPORT,
      imports: '',
      create: ccfRoutes.COMPANY_REPORT_SETUP_WELCOME,
      setup: ccfRoutes.COMPANY_REPORT_SETUP,
      metric: '',
    },
    purchase: {
      list: purchasedGoodsRoutes.PURCHASE_REPORTS,
      report: purchasedGoodsRoutes.PURCHASE_REPORT,
      imports: purchasedGoodsRoutes.PURCHASED_GOODS_IMPORTS_LIST,
      create: purchasedGoodsRoutes.PURCHASE_REPORT_CREATE,
      setup: purchasedGoodsRoutes.PURCHASE_REPORT_CREATE_SETUP,
      metric: purchasedGoodsRoutes.PURCHASE_REPORT_CREATE_METRIC,
      edit: purchasedGoodsRoutes.PURCHASE_REPORT_EDIT_SETUP,
      editMetric: purchasedGoodsRoutes.PURCHASE_REPORT_EDIT_METRIC,
      foodData: purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK,
      packagingData: purchasedGoodsRoutes.PURCHASED_PACKAGING,
      publish: purchasedGoodsRoutes.PURCHASE_REPORT_PUBLISH,

      foodItem: purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM,
      foodImport: purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT,
      foodImportUpload: purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT_ORGANISE,
      foodReview: purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_REVIEW,

      packagingImport: purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT,
      packagingImportUpload:
        purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT_ORGANISE,

      importList: purchasedGoodsRoutes.PURCHASED_GOODS_IMPORTS_LIST,
    },
    sales: {
      list: soldGoodsRoutes.SALES_REPORTS,
      report: soldGoodsRoutes.SALES_REPORT,
      imports: soldGoodsRoutes.SOLD_GOODS_IMPORTS_LIST,
      create: soldGoodsRoutes.SALES_REPORT_CREATE,
      setup: soldGoodsRoutes.SALES_REPORT_CREATE_SETUP,
      metric: soldGoodsRoutes.SALES_REPORT_CREATE_METRIC,
      edit: soldGoodsRoutes.SALES_REPORT_EDIT_SETUP,
      editMetric: soldGoodsRoutes.SALES_REPORT_EDIT_METRIC,

      foodItem: soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM,
      foodData: soldGoodsRoutes.SOLD_FOOD_AND_DRINK,
      packagingData: soldGoodsRoutes.SOLD_PACKAGING,
      foodReview: soldGoodsRoutes.SOLD_FOOD_AND_DRINK_REVIEW,

      publish: soldGoodsRoutes.SALES_REPORT_PUBLISH,

      foodImport: soldGoodsRoutes.SOLD_GOODS_IMPORT,
      foodImportUpload: soldGoodsRoutes.SOLD_GOODS_IMPORT_ORGANISE,

      packagingImport: soldGoodsRoutes.SOLD_GOODS_IMPORT,
      packagingImportUpload: soldGoodsRoutes.SOLD_GOODS_IMPORT_ORGANISE,

      importList: soldGoodsRoutes.SOLD_GOODS_IMPORTS_LIST,
    },
  };
}

export function roundToNiceNumber(num: number) {
  if (num <= 0) return 0;

  // Add 20% padding
  const paddedNum = num * 1.2;

  if (paddedNum < 5) {
    // Round to nearest integer if less than 5
    return Math.ceil(paddedNum);
  } else if (paddedNum < 500) {
    // Round to nearest 10 if less than 500
    return Math.ceil(paddedNum / 10) * 10;
  } else {
    // Round to nearest 100 if 500 or more
    return Math.ceil(paddedNum / 100) * 100;
  }
}

/**
 * Returns `count` evenly-spaced tick values starting from 0, derived from
 * a "nice" step size computed from dataMax. The step is rounded up to the
 * nearest 1, 2, or 5 × power of 10, so ticks are always round numbers
 * (e.g. 0, 1000, 2000, 3000… rather than 0, 1160, 2320…).
 * Handles zero/empty data gracefully by returning a simple [0,1,...] fallback.
 */
export function generateEvenTicks(
  dataMax: number,
  count: number = 6,
  dataMin: number = 0
): number[] {
  if (dataMax <= dataMin) return Array.from({ length: count }, (_, i) => i);
  const intervals = count - 1;
  const rawStep = (dataMax - dataMin) / intervals;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalised = rawStep / magnitude;
  let niceStep: number;
  if (normalised <= 1) niceStep = 1 * magnitude;
  else if (normalised <= 2) niceStep = 2 * magnitude;
  else if (normalised <= 5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;
  const niceMin = Math.floor(dataMin / niceStep) * niceStep;
  return Array.from({ length: count }, (_, i) =>
    parseFloat((niceMin + i * niceStep).toFixed(10))
  );
}

import { usePurchaseReportResult } from '@app/purchased-goods/report/purchase-report-result-context';
import { useSalesReportResult } from '@app/sold-goods/report/sales-report-result-context';
import {
  OperationReportList,
  OperationReportTypeEnum,
  PurchaseReportResultData,
} from '@shared/api/types';
import { useMemo, useState } from 'react';

type ReportFiltersType = 'time_periods' | 'tags' | 'sites' | 'customers';

export interface ReportFilters {
  uuid: string;
  value: string;
  type: ReportFiltersType;
}

export function useSortReportFilters(filters: ReportFilters[] = []) {
  const sortedFilters = useMemo(() => {
    return filters.reduce(
      (acc, filter) => {
        if (filter.type === 'time_periods') {
          acc.timePeriods.push(filter);
        }

        if (filter.type === 'tags') {
          acc.tags.push(filter);
        }

        if (filter.type === 'sites') {
          acc.sites.push(filter);
        }

        if (filter.type === 'customers') {
          acc.customers.push(filter);
        }

        return acc;
      },
      {
        timePeriods: [] as ReportFilters[],
        tags: [] as ReportFilters[],
        customers: [] as ReportFilters[],
        sites: [] as ReportFilters[],
      }
    );
  }, [filters]);

  return sortedFilters;
}

export function useReportFilterParams(filters: ReportFilters[] = []) {
  const { timePeriods, tags, sites, customers } = useSortReportFilters(filters);
  const urlParams = new URLSearchParams();
  timePeriods.length > 0 &&
    urlParams.append('period', timePeriods.map((tp) => tp.uuid).join(','));
  tags.length > 0 &&
    urlParams.append('tag', tags.map((tag) => tag.uuid).join(','));
  sites.length > 0 &&
    urlParams.append('site', sites.map((site) => site.uuid).join(','));
  customers.length > 0 &&
    urlParams.append(
      'customer',
      customers.map((customer) => customer.uuid).join(',')
    );

  return urlParams;
}

export function useGetReportTags(
  report: OperationReportList | PurchaseReportResultData
) {
  const { includesFood, includesPackaging, filters } = report;
  const sortedFilters = useSortReportFilters(filters as ReportFilters[]);

  const goodsTypes =
    includesFood && includesPackaging
      ? []
      : [
          ...(includesFood ? ['Food & drink'] : []),
          ...(includesPackaging ? ['Packaging'] : []),
        ];

  return [
    ...goodsTypes,
    ...sortedFilters.sites.map((site) => site.value),
    ...sortedFilters.customers.map((customer) => customer.value),
    ...sortedFilters.timePeriods.map((tp) => tp.value),
    ...sortedFilters.tags.map((tag) => tag.value),
  ];
}

export const emissionsChartVariants = (dimension = 'month') => ({
  emissions: `Emissions per ${dimension}`,
  intensity: 'Emissions vs carbon intensity',
  goodsType: 'Emissions by goods type',
  spend: 'Emissions vs spend',
});

export function useEmissionsChartVariants(
  reportType: OperationReportTypeEnum,
  dimension = 'month'
) {
  const variants = emissionsChartVariants(dimension);
  const [variant, setVariant] = useState<string>(variants.emissions);
  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;

  const data = useGetReportSectionData(reportType);

  const {
    result: { foodAndDrink, packaging },
  } = data;

  const availableVariants = Object.values(variants).reduce<string[]>(
    (acc, v) => {
      if (v === variants.goodsType && (!foodAndDrink || !packaging)) {
        return acc;
      }
      if (v === variants.spend && !hasCostFeature) {
        return acc;
      }
      acc.push(v);
      return acc;
    },
    []
  );
  return { variant, setVariant, availableVariants, variants };
}

export function useGetReportSectionData(reportType: OperationReportTypeEnum) {
  const purchaseReportResult = usePurchaseReportResult();
  const salesReportResult = useSalesReportResult();

  return reportType === OperationReportTypeEnum.purchase
    ? purchaseReportResult
    : salesReportResult;
}

export type ReportTypeCopy = {
  singular: string;
  plural: string;
  past: string;
  verb: string;
};

export const useGetReportTypeCopy = (
  reportType: OperationReportTypeEnum
): ReportTypeCopy =>
  useMemo(() => {
    switch (reportType) {
      case OperationReportTypeEnum.purchase:
        return {
          singular: 'purchase',
          plural: 'purchases',
          past: 'purchased',
          verb: 'purchase',
        };
      case OperationReportTypeEnum.sales:
        return {
          singular: 'sale',
          plural: 'sales',
          past: 'sold',
          verb: 'sell',
        };
    }
  }, [reportType]);
